import { useEffect } from "react";
import { useContent } from "../contexts/ContentContext";
import { resolveAssetUrl } from "../lib/api";

// Set <title>, meta description, canonical, hreflang, Open Graph, Twitter cards and JSON-LD
// restaurant schema based on current site content + language. Runs client-side but persists in
// the DOM so crawlers that execute JS (Googlebot) and sharing link previews that re-fetch
// pick it up.
export default function SeoHead() {
    const { t, content, lang } = useContent();

    useEffect(() => {
        if (!content || !t) return;
        const siteUrl = window.location.origin;
        const pageUrl = `${siteUrl}/`;
        const heroImage = resolveAssetUrl(content.hero_image);
        const descriptions = {
            ca: "Zarautz, taverna basca i catalana al barri de Sants de Barcelona. Carta de temporada, pintxos, menús de grup i reserves online.",
            es: "Zarautz, taberna vasca y catalana en el barrio de Sants de Barcelona. Carta de temporada, pintxos, menús de grupo y reservas online.",
            en: "Zarautz, a Basque-Catalan tavern in the Sants neighbourhood of Barcelona. Seasonal menu, pintxos, group menus and online booking.",
        };
        const titles = {
            ca: "Zarautz · Taverna Vasca a Barcelona",
            es: "Zarautz · Taberna Vasca en Barcelona",
            en: "Zarautz · Basque Tavern in Barcelona",
        };
        const title = titles[lang] || titles.ca;
        const description = descriptions[lang] || descriptions.ca;

        document.title = title;
        document.documentElement.lang = lang;

        const setMeta = (selector, attr, value) => {
            let el = document.head.querySelector(selector);
            if (!el) {
                el = document.createElement("meta");
                const parts = selector.match(/\[([^=]+)="([^"]+)"\]/);
                if (parts) el.setAttribute(parts[1], parts[2]);
                document.head.appendChild(el);
            }
            el.setAttribute(attr, value);
        };

        setMeta('meta[name="description"]', "content", description);
        setMeta('meta[property="og:title"]', "content", title);
        setMeta('meta[property="og:description"]', "content", description);
        setMeta('meta[property="og:type"]', "content", "restaurant");
        setMeta('meta[property="og:url"]', "content", pageUrl);
        setMeta('meta[property="og:locale"]', "content", lang === "ca" ? "ca_ES" : lang === "es" ? "es_ES" : "en_GB");
        setMeta('meta[property="og:site_name"]', "content", "Zarautz Taberna Vasca");
        if (heroImage) setMeta('meta[property="og:image"]', "content", heroImage);
        setMeta('meta[name="twitter:card"]', "content", "summary_large_image");
        setMeta('meta[name="twitter:title"]', "content", title);
        setMeta('meta[name="twitter:description"]', "content", description);
        if (heroImage) setMeta('meta[name="twitter:image"]', "content", heroImage);

        // canonical
        let canonical = document.head.querySelector('link[rel="canonical"]');
        if (!canonical) {
            canonical = document.createElement("link");
            canonical.setAttribute("rel", "canonical");
            document.head.appendChild(canonical);
        }
        canonical.setAttribute("href", pageUrl);

        // hreflang (same URL for all 3, alternate annotations only)
        document.head.querySelectorAll('link[rel="alternate"][hreflang]').forEach((el) => el.remove());
        ["ca", "es", "en", "x-default"].forEach((hl) => {
            const link = document.createElement("link");
            link.setAttribute("rel", "alternate");
            link.setAttribute("hreflang", hl);
            link.setAttribute("href", pageUrl);
            document.head.appendChild(link);
        });

        // JSON-LD Restaurant schema
        const info = content.contact_info || {};
        const hours = content.hours || {};
        const dayMap = { mon: "Mo", tue: "Tu", wed: "We", thu: "Th", fri: "Fr", sat: "Sa", sun: "Su" };
        const openingHours = Object.entries(hours)
            .filter(([, v]) => v && !/tancat|cerrado|closed/i.test(v))
            .flatMap(([d, v]) => {
                const day = dayMap[d];
                const ranges = Array.from(v.matchAll(/(\d{1,2}:\d{2})\s*-\s*(\d{1,2}:\d{2})/g));
                return ranges.map((m) => `${day} ${m[1]}-${m[2]}`);
            });

        const ld = {
            "@context": "https://schema.org",
            "@type": "Restaurant",
            "@id": pageUrl,
            name: "Zarautz Taberna Vasca",
            description,
            url: pageUrl,
            telephone: info.phone,
            email: info.email,
            image: heroImage,
            servesCuisine: ["Basque", "Catalan", "Mediterranean"],
            priceRange: "€€",
            address: {
                "@type": "PostalAddress",
                streetAddress: info.address,
                addressLocality: "Barcelona",
                addressRegion: "Catalunya",
                addressCountry: "ES",
            },
            sameAs: [info.instagram, info.facebook].filter(Boolean),
            openingHours,
            acceptsReservations: true,
            potentialAction: {
                "@type": "ReserveAction",
                target: content.covermanager_url,
                name: "Book a table",
            },
        };

        let script = document.head.querySelector('script[type="application/ld+json"][data-seo="restaurant"]');
        if (!script) {
            script = document.createElement("script");
            script.type = "application/ld+json";
            script.setAttribute("data-seo", "restaurant");
            document.head.appendChild(script);
        }
        script.textContent = JSON.stringify(ld);
    }, [content, t, lang]);

    return null;
}
